"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { Mail, Phone, Linkedin, Twitter } from "lucide-react";

function TeamMemberContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-4xl">
          <div className="h-64 w-full animate-pulse rounded-xl bg-muted/30" />
          <div className="mt-8 space-y-4">
            <div className="h-8 w-3/4 animate-pulse rounded-xl bg-muted/30" />
            <div className="h-6 w-1/2 animate-pulse rounded-xl bg-muted/30" />
            <div className="h-32 w-full animate-pulse rounded-xl bg-muted/30" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-muted-foreground">Team member not found.</p>
        </div>
      </div>
    );
  }

  const member = posts[0];
  const name = member.customFields?.name || member.title || "";
  const role = member.customFields?.role || "";
  const photo = member.customFields?.photo || member.featuredImage || "";
  const bio = member.customFields?.bio || member.content || "";

  return (
    <section className="bg-background py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-4xl">
          <Card className="overflow-hidden bg-light-background/60 backdrop-blur-md border border-border rounded-xl shadow-soft">
            {photo && (
              <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                <img
                  src={photo}
                  alt={name}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            
            <CardContent className="space-y-6 pt-8">
              <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                {photo && (
                  <Avatar className="size-24 border-2 border-primary shadow-medium">
                    <AvatarImage src={photo} alt={name} />
                    <AvatarFallback className="bg-primary-light text-primary text-2xl font-semibold">
                      {name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                )}
                
                <div className="flex-1 text-center sm:text-left">
                  <h1 className="font-display text-3xl font-bold text-foreground">
                    {name}
                  </h1>
                  {role && (
                    <p className="mt-2 text-lg font-medium text-primary">
                      {role}
                    </p>
                  )}
                </div>
              </div>

              {bio && (
                <>
                  <Separator className="my-6 bg-border" />
                  <div>
                    <h2 className="mb-4 font-display text-xl font-semibold text-foreground">
                      Biography
                    </h2>
                    <div
                      className="prose prose-gray max-w-none whitespace-pre-wrap text-foreground-light"
                      dangerouslySetInnerHTML={{ __html: bio }}
                    />
                  </div>
                </>
              )}

              <Separator className="my-6 bg-border" />

              <div className="space-y-4">
                <h2 className="font-display text-xl font-semibold text-foreground">
                  Contact Information
                </h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-3 rounded-xl bg-light-background/60 backdrop-blur-md border border-border p-4 shadow-soft">
                    <Phone className="size-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-muted">Phone</p>
                      <Link
                        to="tel:(555) 555-1234"
                        className="text-foreground hover:text-accent-dark transition-colors duration-[var(--duration-medium)]"
                      >
                        (555) 555-1234
                      </Link>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 rounded-xl bg-light-background/60 backdrop-blur-md border border-border p-4 shadow-soft">
                    <Mail className="size-5 shrink-0 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-muted">Email</p>
                      <Link
                        to="mailto:contact@aaeagleplumbing.com"
                        className="text-foreground hover:text-accent-dark transition-colors duration-[var(--duration-medium)]"
                      >
                        contact@aaeagleplumbing.com
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4 pt-4 sm:justify-start">
                <Link
                  to="#"
                  className="inline-flex items-center gap-2 rounded-xl bg-accent text-foreground-accent px-4 py-2 font-semibold shadow-soft transition-all duration-[var(--duration-medium)] hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:text-foreground active:translate-y-0"
                  aria-label="LinkedIn profile"
                >
                  <Linkedin className="size-5" />
                  <span className="text-sm">LinkedIn</span>
                </Link>
                <Link
                  to="#"
                  className="inline-flex items-center gap-2 rounded-xl bg-accent text-foreground-accent px-4 py-2 font-semibold shadow-soft transition-all duration-[var(--duration-medium)] hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:text-foreground active:translate-y-0"
                  aria-label="Twitter profile"
                >
                  <Twitter className="size-5" />
                  <span className="text-sm">Twitter</span>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default function SingleTeamMemberTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <PostsProvider postType="team_member" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia"]} fields={["id", "title.rendered", "content.rendered", "featured_media", "_embedded", "meta", "_links.wp:featuredmedia"]}>
      <TeamMemberContent />
    </PostsProvider>
  );
}